/**
 * Верхний переключатель этапов проекта в 2D-редакторе.
 *
 * Позволяет одним кликом переключаться между рабочими планами:
 *   • «Исходный план» — обмерный план до демонтажа и монтажа;
 *   • «Демонтаж» — выбор и подсветка сносимых перегородок по клику;
 *   • «Монтаж» — возведение новых перегородок с пунктирным контуром снесённых стен;
 *   • «Мебель», «Пол», «Гидроизоляция», «Сантехника», «Электрика», «Все слои» —
 *     работают с реальной картиной стен (`Исходный план − Демонтаж + Монтаж`) и
 *     автоматически включают нужные слои чертежа.
 */
import { EDITOR_OVERLAY_PAINT_STYLE } from "@/shared/config/layout";
import { CatalogSectionKind, LayerKind } from "@/shared/plan";
import { setVisibleLayers, useProjectStore } from "@/shared/store";
import { UIKit } from "@/shared/ui";
import { DrawerSection, Tool, useEditorStore, type PlanStage } from "@/widgets/editor";

interface StageDefinition {
	id: PlanStage;
	label: string;
	title: string;
	layers: readonly LayerKind[];
	catalog?: CatalogSectionKind;
}

const STAGES: readonly StageDefinition[] = [
	{
		id: "initial",
		label: "Исходный план",
		title: "Обмерный план до демонтажа и монтажа перегородок",
		layers: [LayerKind.Walls],
	},
	{
		id: "demolition",
		label: "Демонтаж",
		title: "План демонтажа перегородок (клик по стене помечает её под снос)",
		layers: [LayerKind.Walls],
	},
	{
		id: "construction",
		label: "Монтаж",
		title: "План возведения новых перегородок",
		layers: [LayerKind.Walls],
	},
	{
		id: "furniture",
		label: "Мебель",
		title: "План расстановки мебели и оборудования",
		layers: [LayerKind.Walls, LayerKind.Furniture],
		catalog: CatalogSectionKind.Furniture,
	},
	{
		id: "floor",
		label: "Пол",
		title: "План напольных покрытий, стяжки и плинтусов",
		layers: [LayerKind.Walls, LayerKind.Floors, LayerKind.Screed, LayerKind.Baseboard],
		catalog: CatalogSectionKind.Floor,
	},
	{
		id: "waterproof",
		label: "Гидроизоляция",
		title: "План гидроизоляции мокрых зон",
		layers: [LayerKind.Walls, LayerKind.Waterproof],
		catalog: CatalogSectionKind.Waterproof,
	},
	{
		id: "plumbing",
		label: "Сантехника",
		title: "План сантехники и разводки труб водоснабжения",
		layers: [LayerKind.Walls, LayerKind.Plumbing, LayerKind.Pipes],
		catalog: CatalogSectionKind.Plumbing,
	},
	{
		id: "electrical",
		label: "Электрика",
		title: "План розеток, выключателей, светильников и кабельных трасс",
		layers: [LayerKind.Walls, LayerKind.Electrical, LayerKind.Wiring],
		catalog: CatalogSectionKind.Electrical,
	},
	{
		id: "all",
		label: "Все слои",
		title: "Показать все слои проекта одновременно",
		layers: [
			LayerKind.Walls,
			LayerKind.Floors,
			LayerKind.Waterproof,
			LayerKind.Screed,
			LayerKind.Baseboard,
			LayerKind.WallFinish,
			LayerKind.Furniture,
			LayerKind.Plumbing,
			LayerKind.Pipes,
			LayerKind.Electrical,
			LayerKind.Wiring,
		],
	},
];

export const PlanStageSwitcher = () => {
	const planStage = useEditorStore((state) => state.planStage);
	const setPlanStage = useEditorStore((state) => state.setPlanStage);
	const setActiveCatalog = useEditorStore((state) => state.setActiveCatalog);
	const setTool = useEditorStore((state) => state.setTool);
	const drawer = useEditorStore((state) => state.drawer);
	const setDrawer = useEditorStore((state) => state.setDrawer);
	const isReadOnly = useEditorStore((state) => state.isReadOnly);
	const top = useEditorStore((state) => state.overlayTopOffset);
	const walls = useProjectStore((state) => state.project.layers.walls.walls);

	const demolishedCount = Object.values(walls).filter((wall) => wall.demolished).length;
	const mountedCount = Object.values(walls).filter(
		(wall) => wall.mounted && !wall.demolished,
	).length;

	const selectStage = (stage: StageDefinition) => {
		setPlanStage(stage.id);
		setVisibleLayers(stage.layers);
		if (stage.catalog) {
			setActiveCatalog(stage.catalog);
		}
		if (stage.id === "demolition") {
			setTool(Tool.Select);
			if (drawer === DrawerSection.Walls) setDrawer(null);
		} else if (stage.id === "construction" && !isReadOnly) {
			// На этапе монтажа перегородок удобно сразу чертить перегородки
			useEditorStore.getState().setWallRole("partition");
		}
	};

	return (
		<div
			data-plan-export-exclude
			className="pointer-events-none absolute inset-x-0 z-10 flex justify-center px-2 pl-18 md:pl-20"
			style={{ top }}
		>
			<div
				className="pointer-events-auto flex max-w-full items-center gap-1 overflow-x-auto rounded-lg border border-border bg-surface/95 p-1 shadow-sm backdrop-blur-sm"
				style={EDITOR_OVERLAY_PAINT_STYLE}
				role="tablist"
				aria-label="Этапы проекта"
			>
				{STAGES.map((stage) => {
					const active = planStage === stage.id;
					const badge =
						stage.id === "demolition" && demolishedCount > 0
							? demolishedCount
							: stage.id === "construction" && mountedCount > 0
								? mountedCount
								: null;
					return (
						<UIKit.Button
							key={stage.id}
							type="button"
							border={false}
							role="tab"
							aria-selected={active}
							title={stage.title}
							className={`flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors ${
								active
									? stage.id === "demolition"
										? "bg-danger text-white shadow-xs hover:bg-danger"
										: stage.id === "construction"
											? "bg-emerald-600 text-white shadow-xs hover:bg-emerald-600"
											: "bg-accent text-overlay-ink shadow-xs hover:bg-accent"
									: "text-ink hover:bg-surface-muted hover:text-accent"
							}`}
							onClick={() => selectStage(stage)}
						>
							<UIKit.Text as="span" size="small" className="text-xs font-medium text-current">
								{stage.label}
							</UIKit.Text>
							{badge !== null && (
								<UIKit.Text
									as="span"
									size="small"
									className={`rounded-full px-1.5 py-0.25 text-xs leading-tight font-semibold tabular-nums ${
										active ? "bg-white/20 text-white" : "bg-surface-muted text-ink"
									}`}
								>
									{badge}
								</UIKit.Text>
							)}
						</UIKit.Button>
					);
				})}
			</div>
		</div>
	);
};
